import { Link } from "@tanstack/react-router";
import { Star, Play, Flame, Eye, MessageSquare } from "lucide-react";
import type { Movie } from "@/lib/movies-data";
import { useTheme } from "@/lib/theme-context";
import { smallImage, tmdbPosterSrcSet, picsumResize, isExternalRemoteImage, weservProxy, weservSrcSet } from "@/lib/image-utils";
import { pickGenres, pickTitle } from "@/lib/i18n";
import { useCardCounts } from "@/lib/firebase-data";

function formatCount(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(n >= 10_000 ? 0 : 1) + "K";
  return String(n);
}

function posterFor(url: string) {
  if (!url) return { src: url, srcSet: undefined as string | undefined };
  if (url.includes("image.tmdb.org")) {
    return { src: smallImage(url, 342, 513), srcSet: tmdbPosterSrcSet(url) };
  }
  if (url.includes("picsum.photos")) {
    return {
      src: picsumResize(url, 342, 513),
      srcSet: [
        `${picsumResize(url, 185, 278)} 185w`,
        `${picsumResize(url, 342, 513)} 342w`,
        `${picsumResize(url, 500, 750)} 500w`,
      ].join(", "),
    };
  }
  if (isExternalRemoteImage(url)) {
    // Resize/re-encode arbitrary CDN images (GitHub raw, custom hosts) via
    // the weserv proxy to avoid shipping multi-MB source files to phones.
    return { src: weservProxy(url, 342), srcSet: weservSrcSet(url, [185, 342, 500]) };
  }
  return { src: url, srcSet: undefined };
}

export function PosterCard({ movie }: { movie: Movie }) {
  const { dir, language } = useTheme();
  const isSeries = movie.type === "series";
  const { src, srcSet } = posterFor(movie.poster);
  const title = pickTitle(movie, language);
  const genres = pickGenres(movie, language);
  const counts = useCardCounts(movie.slug, `${isSeries ? "series" : "movie"}-${movie.slug}`);
  void dir;

  return (
    <Link
      to={isSeries ? "/series/$slug" : "/movie/$slug"}
      params={{ slug: movie.slug }}
      className="poster-card group block animate-fade-in"
    >
      <div className="aspect-[2/3] relative">
        <img
          src={src}
          srcSet={srcSet}
          sizes="(max-width: 640px) 45vw, (max-width: 1024px) 30vw, 220px"
          alt={title}
          width={342}
          height={513}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        {/* Badges */}
        <div className="absolute top-2 start-2 flex flex-col gap-1 z-10">
          {movie.isNew && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-primary text-primary-foreground">
              {(language === "ar" ? "\u062c\u062f\u064a\u062f" : language === "fr" ? "NOUVEAU" : "NEW")}
            </span>
          )}
          {movie.isTrending && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md bg-foreground/90 text-background">
              <Flame className="h-2.5 w-2.5" /> {(language === "ar" ? "\u0631\u0627\u0626\u062c" : language === "fr" ? "TENDANCE" : "HOT")}
            </span>
          )}
        </div>
        {/* Rating */}
        <div className="absolute top-2 end-2 z-10 flex items-center gap-1 px-2 py-0.5 rounded-md bg-background text-foreground border border-border text-xs font-bold shadow-sm">
          <Star className="h-3 w-3 fill-[var(--rating)] text-[var(--rating)]" aria-hidden />
          <span aria-label={`Rating ${movie.rating.toFixed(1)} of 10`}>{movie.rating.toFixed(1)}</span>
        </div>
        {/* Hover play overlay (desktop only — saves paint on mobile) */}
        <div className="hidden md:grid absolute inset-0 place-items-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
          <div className="grid place-items-center w-14 h-14 rounded-full glass">
            <Play className="h-6 w-6 fill-primary text-primary translate-x-0.5" />
          </div>
        </div>
        {/* Bottom gradient for legibility */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 z-[5]"
          style={{ background: "linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.75) 35%, rgba(0,0,0,0.35) 70%, rgba(0,0,0,0) 100%)" }}
          aria-hidden
        />
        {/* Title overlay */}
        <div className="absolute bottom-0 inset-x-0 p-3 z-10">
          <h3 className="font-bold text-sm text-white line-clamp-1 drop-shadow-lg">
            {title}
          </h3>
          <div className="flex items-center gap-2 text-[11px] text-white/85 mt-0.5">
            <span>{movie.year}</span>
            <span>•</span>
            <span>{movie.type === "movie" ? ((language === "ar" ? "\u0641\u064a\u0644\u0645" : language === "fr" ? "Film" : "Movie")) : ((language === "ar" ? "\u0645\u0633\u0644\u0633\u0644" : language === "fr" ? "S\u00e9rie" : "Series"))}</span>
            <span>•</span>
            <span className="line-clamp-1">{genres[0]}</span>
          </div>
          <div className="flex items-center gap-3 text-[10.5px] text-white/85 mt-1 font-semibold">
            <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {counts ? formatCount(counts.views) : "\u2026"}</span>
            <span className="inline-flex items-center gap-1"><MessageSquare className="h-3 w-3" /> {counts ? formatCount(counts.comments) : "\u2026"}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
